import { parseScoreboardIntel } from './espnIntel';
import type { Game, GameStatus } from './types';

const SCOREBOARD_URL =
  'https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard';

export const DEFAULT_WEEK_RANGE = '20260909-20260915';

interface EspnScoreboard {
  week?: { number?: number };
  season?: { year?: number };
  events?: EspnEvent[];
}

interface EspnEvent {
  id: string;
  date: string;
  status?: { type?: { name?: string; state?: string; completed?: boolean } };
  competitions?: EspnCompetition[];
}

interface EspnCompetition {
  date?: string;
  competitors?: EspnCompetitor[];
}

interface EspnCompetitor {
  id: string;
  homeAway: 'home' | 'away';
  team?: { id?: string; displayName?: string; abbreviation?: string; logo?: string };
  score?: string;
}

function mapStatus(event: EspnEvent): GameStatus {
  const state = event.status?.type?.state ?? event.status?.type?.name ?? '';
  if (state === 'post' || event.status?.type?.completed) return 'final';
  if (state === 'in' || state === 'halftime') return 'in';
  return 'scheduled';
}

function parseScore(value?: string): number | undefined {
  if (value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function teamNicknameSlug(name: string): string {
  const nick = name.trim().split(/\s+/).at(-1) ?? name;
  return nick.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function espnGameUrl(game: Pick<Game, 'id' | 'away' | 'home'>): string {
  const slug = `${teamNicknameSlug(game.away.name)}-${teamNicknameSlug(game.home.name)}`;
  return `https://www.espn.com/nfl/game/_/gameId/${game.id}/${slug}`;
}

export function parseEspnEvents(events: EspnEvent[]): Game[] {
  const games: Game[] = [];
  for (const event of events) {
    const comp = event.competitions?.[0];
    if (!comp?.competitors?.length) continue;
    const away = comp.competitors.find((c) => c.homeAway === 'away');
    const home = comp.competitors.find((c) => c.homeAway === 'home');
    if (!away?.team || !home?.team) continue;

    const kickoffUtc = comp.date ?? event.date;
    const status = mapStatus(event);
    const awayScore = parseScore(away.score);
    const homeScore = parseScore(home.score);

    games.push({
      id: event.id,
      kickoffUtc,
      away: {
        id: away.team.id ?? away.id,
        name: away.team.displayName ?? 'Away',
        short: away.team.abbreviation ?? 'AWY',
        logo: away.team.logo,
      },
      home: {
        id: home.team.id ?? home.id,
        name: home.team.displayName ?? 'Home',
        short: home.team.abbreviation ?? 'HME',
        logo: home.team.logo,
      },
      status,
      awayScore: status === 'scheduled' ? undefined : awayScore,
      homeScore: status === 'scheduled' ? undefined : homeScore,
      intel: parseScoreboardIntel(event),
    });
  }

  return games.sort(
    (a, b) => Date.parse(a.kickoffUtc) - Date.parse(b.kickoffUtc),
  );
}

export interface ScoreboardWeek {
  games: Game[];
  weekKey: string;
  dateRange: string;
}

export function weekScoreboardUrl(week: number, year = 2026): string {
  return `${SCOREBOARD_URL}?week=${week}&year=${year}&seasontype=2`;
}

async function readScoreboard(url: string, dateRange?: string): Promise<ScoreboardWeek> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`ESPN scoreboard failed (${res.status})`);
  }
  const data = (await res.json()) as EspnScoreboard;
  const events = data.events ?? [];
  if (events.length === 0) {
    throw new Error('No games returned for this week');
  }
  const year = data.season?.year ?? 2026;
  const week = data.week?.number ?? 1;
  return {
    games: parseEspnEvents(events),
    weekKey: `${year}-W${week}`,
    dateRange: dateRange ?? `${year}-W${week}`,
  };
}

export async function fetchScoreboard(dates?: string): Promise<ScoreboardWeek> {
  const url = dates ? `${SCOREBOARD_URL}?dates=${dates}` : SCOREBOARD_URL;
  return readScoreboard(url, dates);
}

export async function fetchWeekScoreboard(week: number, year = 2026): Promise<ScoreboardWeek> {
  return readScoreboard(weekScoreboardUrl(week, year));
}

export async function fetchWeekGames(dateRange: string): Promise<Game[]> {
  const board = await fetchScoreboard(dateRange);
  return board.games;
}
